(() => {
  'use strict';
  if (window.CandyPeakCartRecommendations) return;
  const slotSelector = '[data-cp-owned-cart-recommendations]';
  const states = new WeakMap();
  const requestCache = new Map();
  const attributionKey = '_cp_recommendation_source';
  const attributionValue = 'cart-carousel-v1';
  const pendingProducts = new Set();
  function cartContainsProduct(cart, product) {
    return cart.items.some(item => String(item.product_id) === String(product.id) || Boolean(product.handle && item.handle === product.handle));
  }
  function reportConfirmedRecommendation(result, product, variant, config) {
    // Consent is checked at the moment of the successful add. Missing/throwing APIs deny tracking.
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
      const lines = Array.isArray(result?.items) ? result.items : [result];
      const confirmed = lines.some(line => line && String(line.variant_id || line.id) === String(variant.id)
        && (line.product_id == null || String(line.product_id) === String(product.id))
        && Number(line.quantity) > 0 && line.properties?.[attributionKey] === attributionValue);
      if (!confirmed || typeof window.crypto?.randomUUID !== 'function') return;
      const productId = String(product.id), variantId = String(variant.id);
      const themeId = String(window.Shopify.theme?.id || '');
      const priceCents = Number(variant.price), currency = String(config.currency || '').toUpperCase();
      if (!/^\d+$/.test(productId) || !/^\d+$/.test(variantId) || !/^\d+$/.test(themeId)
        || !Number.isSafeInteger(priceCents) || priceCents < 0 || !/^[A-Z]{3}$/.test(currency)) return;
      const payload = {
        eventId: window.crypto.randomUUID(), productId, variantId,
        title: String(product.title || '').slice(0, 200), quantity: 1, priceCents, currency,
        themeId, consent: true,
        environment: window.Shopify.designMode || window.Shopify.theme?.role !== 'main' ? 'preview' : 'live'
      };
      // Fire once, without cookies or referrer. Never await/retry this optional analytics request.
      void fetch('https://candy-peak-control-center.marionovembre.chatgpt.site/api/upsells/collect', {
        method:'POST', mode:'cors', credentials:'omit', referrerPolicy:'no-referrer', keepalive:true,
        headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload)
      }).catch(() => {});
    } catch { /* Tracking cannot change the cart result. */ }
  }
  async function addRecommendation(product, variant, config, button) {
    const productID = String(product.id);
    if (pendingProducts.has(productID)) return;
    pendingProducts.add(productID);
    try {
      // Prevent a stale recommendation from creating a second, differently tagged
      // line for a product that was already added normally. Never retag existing lines.
      const response = await fetch(new URL(config.root + 'cart.js', location.origin), {
        credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}
      });
      if (!response.ok) throw new Error('Could not verify the current cart');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      if (cartContainsProduct(cart, product)) {
        await window.CandyPeakOwnedCart.refresh();
        return;
      }
      const result = await window.CandyPeakOwnedCart.addItem({
        id:variant.id,quantity:1,properties:{[attributionKey]:attributionValue}
      },button);
      reportConfirmedRecommendation(result,product,variant,config);
      return result;
    } finally { pendingProducts.delete(productID); }
  }

  const collectionGroups = {
    'xxl-squishies':'xxl', 'wax-cracking-squishies':'wax', dumplings:'dumpling', squishys:'squishy',
    chips:'snack', 'snack-boxen':'snack', schokolade:'snack', 'snacks-1':'snack', getranke:'drink',
    'geschmacks-zahnstocher':'tooth', 'z-z-zahnstocher-😮‍💨':'tooth'
  };
  function groups(product) {
    const result = new Set();
    (product.collections || []).forEach(handle => { if (collectionGroups[handle]) result.add(collectionGroups[handle]); });
    const tags = (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',')).map(t => t.trim().toLowerCase());
    if (tags.includes('cp-cat-xxl')) result.add('xxl');
    if (tags.includes('cp-cat-wax')) result.add('wax');
    if (tags.includes('cp-cat-dumplings')) result.add('dumpling');
    const identity = `${product.handle || ''} ${product.title || ''}`.toLowerCase();
    if (result.has('xxl') || result.has('wax') || result.has('dumpling') || /squish/.test(identity)) result.add('squishy');
    if (/zahnstocher|toothpick/.test(identity)) result.add('tooth');
    if (!result.has('squishy') && !result.has('tooth') && /getränk|getr[aä]nke|softdrink|energy drink/.test(tags.join(' '))) result.add('drink');
    // Broad Snacks membership may include drinks/toys. The specific category wins.
    if (result.has('drink') || result.has('squishy') || result.has('tooth')) result.delete('snack');
    return result;
  }
  function technical(product) {
    return product.technical || ['deposit', 'drinkbox-deposit'].includes(product.properties?._cpLineType) || String(product.properties?._cpDrinkboxDeposit) === 'true' || /(?:^|[-\s])(?:pfand|deposit)(?:$|[-\s])/i.test(`${product.handle || ''} ${product.title || ''}`);
  }
  function normalize(product) {
    if (!product || !product.id || technical(product) || !product.available) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const available = variants.filter(v => v.available && Number.isFinite(Number(v.price)) && Number(v.price) > 0);
    if (!available.length) return null;
    const prices = available.map(v => Number(v.price));
    return { ...product, id:String(product.id), groups:groups(product), price:Math.min(...prices),
      priceVaries:new Set(prices).size > 1, cheapestVariant:available.find(v => Number(v.price) === Math.min(...prices)),
      quickVariant:variants.length === 1 && available.length === 1 && !product.requires_selling_plan && !available[0].sellingPlanRequired ? available[0] : null };
  }
  function choose(products, items, limit = 8, context = {}) {
    limit = Math.max(2, Math.min(8, Number(context.limit) || limit));
    const excluded = new Set((context.excludedIds || []).map(String));
    const preferred = new Set((context.preferredIds || []).map(String));
    const manual = context.mode === 'selected_only';
    const ids = new Set(items.map(p => String(p.product_id || p.id)));
    const handles = new Set(items.map(p => p.handle).filter(Boolean));
    const seeds = items.filter(p => !technical(p)).map(p => ({...p, groups:groups(p)}));
    if (!seeds.length) return [];
    const seen = new Set();
    const ranked = [];
    products.forEach((raw, order) => {
      const p = normalize(raw);
      if (!p || excluded.has(p.id) || (manual && !preferred.has(p.id)) || ids.has(p.id) || handles.has(p.handle) || seen.has(p.id)) return;
      seen.add(p.id);
      let score = manual ? 100 : 0;
      seeds.forEach(seed => {
        const a = seed.groups, b = p.groups;
        if (a.has('squishy') && b.has('squishy')) {
          const subtypes = ['xxl','wax','dumpling'];
          const known = subtypes.some(type => a.has(type));
          const newType = subtypes.some(type => b.has(type) && !a.has(type));
          score = Math.max(score, known && newType ? 100 : 75);
        } else if (a.has('tooth') && b.has('tooth')) score = Math.max(score, 100);
        else if ((a.has('snack') && b.has('drink')) || (a.has('drink') && b.has('snack'))) score = Math.max(score, 90);
        else if ((a.has('snack') && b.has('snack')) || (a.has('drink') && b.has('drink'))) score = Math.max(score, 75);
      });
      if (score) ranked.push({...p, score:score + (preferred.has(p.id) ? 20 : 0) + (p.fromRecommendations ? 5 : 0), order});
    });
    ranked.sort((a,b) => b.score - a.score || a.order - b.order);
    // Spread the first suggestions across relevant families/subtypes before repeating one.
    const selected = [], used = new Set();
    const remaining = Number(context.shippingThreshold) - Number(context.cartTotal);
    const nearShipping = context.shippingPriority !== false && Number.isSafeInteger(remaining) && remaining > 0 && remaining <= (Number(context.shippingWindow) || 1500)
      ? ranked.filter(p => p.quickVariant && p.price >= remaining && p.price <= remaining + (Number.isFinite(Number(context.shippingOverrun)) ? Math.max(0, Number(context.shippingOverrun)) : 500))
          .sort((a,b) => a.price - b.price || b.score - a.score || a.order - b.order)[0]
      : null;
    const kind = p => ['wax','xxl','dumpling','tooth','drink','snack','squishy'].find(g => p.groups.has(g)) || '';
    if (nearShipping) { selected.push({...nearShipping, shippingMatch:true}); used.add(kind(nearShipping)); }
    for (const p of ranked) {
      if (selected.some(s => s.id === p.id)) continue;
      if (!used.has(kind(p))) { selected.push(p); used.add(kind(p)); }
      if (selected.length === limit) return selected;
    }
    for (const p of ranked) {
      if (!selected.some(s => s.id === p.id)) selected.push(p);
      if (selected.length === limit) break;
    }
    return selected;
  }
  function safeURL(value, root, productOnly = false) {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value, location.origin + root);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      if (productOnly && (url.origin !== location.origin || !url.pathname.includes('/products/'))) return null;
      if (productOnly && root !== '/' && url.pathname.startsWith('/products/')) url.pathname = root + url.pathname.slice(1);
      if (!productOnly && url.origin !== location.origin && !url.hostname.endsWith('.shopify.com') && url.hostname !== 'cdn.shopify.com') return null;
      return url.href;
    } catch { return null; }
  }
  function node(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  }
  function render(slot, config, products) {
    const content = slot.querySelector('[data-cp-rec-content]');
    if (!content) return;
    const signature = JSON.stringify([config.cartTotal,config.shippingThreshold,products.map(p => [p.id,p.title,p.variants,p.shippingMatch])]);
    if (content.dataset.signature === signature) return;
    // Do not replace an active variant picker/button when optional API results arrive.
    if (content.contains(document.activeElement) && content.dataset.signature) return;
    const priorChoices = new Map([...content.querySelectorAll('select[data-product]')].map(el => [el.dataset.product,el.value]));
    content.dataset.signature = signature;
    content.replaceChildren();
    content.hidden = !products.length;
    if (!products.length) return;
    const money = value => {
      try { return new Intl.NumberFormat(config.locale,{style:'currency',currency:config.currency}).format(Number(value)/100); }
      catch { return (Number(value)/100).toFixed(2)+' '+config.currency; }
    };
    content.append(node('h3','',config.labels.heading));
    const carousel=node('div','cp-owned-cart-recommendations__carousel');
    const previous=node('button','cp-owned-cart-recommendations__arrow','‹'); previous.type='button'; previous.setAttribute('aria-label',config.labels.previous);
    const next=node('button','cp-owned-cart-recommendations__arrow','›'); next.type='button'; next.setAttribute('aria-label',config.labels.next);
    const list=node('ul','cp-owned-cart-recommendations__list'); list.setAttribute('aria-label',config.labels.heading);
    products.forEach(p => {
      const href=safeURL(p.url || `${config.root}products/${encodeURIComponent(p.handle)}`,config.root,true);
      if (!href) return;
      const available=p.variants.filter(v => v.available && Number(v.price)>0);
      const requiresPlan=p.requires_selling_plan || available.some(v => v.sellingPlanRequired);
      const canAdd=!requiresPlan && typeof window.CandyPeakOwnedCart?.addItem==='function';
      const needsChoice=p.variants.length>1;
      let selected=needsChoice ? available.find(v => String(v.id)===priorChoices.get(p.id)) : available[0];
      const row=node('li','cp-owned-cart-recommendations__item');
      const imageLink=node('a','cp-owned-cart-recommendations__image-link'); imageLink.href=href; imageLink.tabIndex=-1; imageLink.setAttribute('aria-hidden','true');
      const imageURL=safeURL(p.image || p.featured_image,config.root);
      if (imageURL) { const img=node('img','cp-owned-cart-recommendations__image'); img.src=imageURL; img.alt='';img.width=80;img.height=80;img.loading='lazy';imageLink.append(img); }
      const copy=node('div','cp-owned-cart-recommendations__copy');
      if (p.shippingMatch && config.labels.shipping_suggestion) copy.append(node('span','cp-owned-cart-recommendations__reason',config.labels.shipping_suggestion));
      const title=node('a','cp-owned-cart-recommendations__title',p.title);title.href=href;title.title=p.title;copy.append(title);
      const prices=node('div','cp-owned-cart-recommendations__prices');
      const price=node('span','cp-owned-cart-recommendations__price');
      const compare=node('s','cp-owned-cart-recommendations__compare');
      const savings=node('span','cp-owned-cart-recommendations__savings');
      const unit=node('span','cp-owned-cart-recommendations__unit');
      prices.append(price,compare,savings);copy.append(prices,unit);
      let select;
      if (canAdd && needsChoice) {
        select=node('select','cp-owned-cart-recommendations__select');select.dataset.product=p.id;
        select.setAttribute('aria-label',config.labels.variant+' – '+p.title);
        const placeholder=node('option','',config.labels.choose_variant);placeholder.value='';placeholder.disabled=true;select.append(placeholder);
        available.forEach(v => {const option=node('option','',v.title);option.value=String(v.id);select.append(option);});
        select.value=selected ? String(selected.id) : '';
        copy.append(select);
      }
      const action=node(canAdd?'button':'a','cp-owned-cart-recommendations__action',canAdd?config.labels.add:config.labels.options);
      if (canAdd) action.type='button';else action.href=href;
      const update=() => {
        const shown=selected || p.cheapestVariant;
        price.textContent=(!selected && p.priceVaries ? config.labels.from+' ' : '')+money(shown.price);
        const compareAt=Number(shown.compareAtPrice ?? shown.compare_at_price);
        const sale=Boolean(selected && compareAt>Number(shown.price));
        compare.hidden=!sale;savings.hidden=!sale;
        compare.textContent=sale?money(compareAt):'';
        savings.textContent=sale?config.labels.saving+' '+money(compareAt-Number(shown.price)):'';
        unit.hidden=!(shown.unitPrice && shown.unitReference);
        unit.textContent=unit.hidden?'':money(shown.unitPrice)+' / '+shown.unitReference;
        if (canAdd) {
          action.toggleAttribute('data-cp-needs-choice', !selected);
          action.disabled=!selected || Boolean(slot.closest('[data-cp-cart-busy],[data-cp-cart-error]'));
        }
      };
      select?.addEventListener('change',() => {selected=available.find(v => String(v.id)===select.value);update();});
      if (canAdd) action.addEventListener('click',async () => {
        if (action.disabled || !selected?.available) return;
        const chosen=selected;action.disabled=true;if(select)select.disabled=true;action.textContent=config.labels.adding;
        copy.querySelector('.cp-owned-cart-recommendations__error')?.remove();
        try {await addRecommendation(p,chosen,config,action);}
        catch {const error=node('p','cp-owned-cart-recommendations__error',config.labels.error);error.setAttribute('role','alert');copy.append(error);}
        finally {if(action.isConnected){if(select)select.disabled=false;action.textContent=config.labels.add;update();}}
      });
      update();copy.append(action);row.append(imageLink,copy);list.append(row);
    });
    const arrows=() => {
      const index=Math.round(list.scrollLeft/Math.max(1,list.clientWidth));
      previous.disabled=index<=0;next.disabled=index>=list.children.length-1;
    };
    const move=direction => list.scrollBy({left:direction*list.clientWidth,behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
    previous.addEventListener('click',()=>move(-1));next.addEventListener('click',()=>move(1));
    list.addEventListener('scroll',arrows,{passive:true});
    carousel.append(previous,list,next);content.append(carousel);requestAnimationFrame(arrows);
  }
  function recommendations(seed, root) {
    const key = `${root}:${seed.id}`;
    const cached = requestCache.get(key);
    if (cached && Date.now() - cached.time < 60000) return cached.promise;
    const endpoint = new URL(`${root}recommendations/products.json`, location.origin);
    endpoint.searchParams.set('product_id',seed.id); endpoint.searchParams.set('limit','8'); endpoint.searchParams.set('intent','complementary');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(),4000);
    const promise = fetch(endpoint.href,{credentials:'same-origin',signal:controller.signal})
      .then(r => r.ok ? r.json() : {products:[]})
      .then(data => Array.isArray(data.products) ? data.products.map(p => ({...p,fromRecommendations:true})) : [])
      .catch(() => []).finally(() => clearTimeout(timer));
    requestCache.set(key,{time:Date.now(),promise});
    if (requestCache.size > 30) requestCache.delete(requestCache.keys().next().value);
    return promise;
  }
  function refresh(cart) {
    document.querySelectorAll(slotSelector).forEach(async slot => {
      let config;
      try { config=JSON.parse(slot.querySelector('[data-cp-rec-config]').textContent); } catch { return; }
      if (!Array.isArray(config.products) || !Array.isArray(config.items)) return;
      config.root = typeof config.root === 'string' && config.root.startsWith('/') && !config.root.startsWith('//') ? config.root : '/';
      if (!config.root.endsWith('/')) config.root += '/';
      if (cart && Number.isSafeInteger(cart.total_price)) config.cartTotal = cart.total_price;
      let items=config.items;
      if (cart && Array.isArray(cart.items)) {
        // Use the actual current IDs even if a caller sends an event before replacing its section.
        items=cart.items.map(item => ({...config.items.find(p => String(p.id) === String(item.product_id)), ...item, id:item.product_id || item.id, handle:item.handle || config.items.find(p => String(p.id) === String(item.product_id))?.handle}));
      }
      const generation=(states.get(slot)?.generation || 0)+1;
      states.set(slot,{generation});
      render(slot,config,choose(config.products,items,8,config));
      const seeds=items.filter(p => !technical(p) && groups(p).size).filter((p,i,a) => a.findIndex(x => String(x.id) === String(p.id)) === i).slice(0,2);
      if (config.mode === 'selected_only') return;
      const responses=await Promise.all(seeds.map(seed => recommendations(seed,config.root)));
      if (!slot.isConnected || states.get(slot)?.generation !== generation) return;
      // Liquid metadata gives exact collection membership; remote results retain it where available.
      const metadata=new Map(config.products.map(p => [String(p.id),p]));
      const remote=responses.flat().map(p => {
        const saved=metadata.get(String(p.id));
        const savedVariants=new Map((saved?.variants || []).map(v => [String(v.id),v]));
        const variants=(Array.isArray(p.variants) ? p.variants : []).map(v => {
          const previous=savedVariants.get(String(v.id));
          const measurement=v.unit_price_measurement;
          const reference=measurement?.reference_unit
            ? (Number(measurement.reference_value) === 1 ? '' : String(measurement.reference_value || '')) + measurement.reference_unit
            : previous?.unitReference;
          return {...previous,...v,
            unitPrice:Object.prototype.hasOwnProperty.call(v,'unit_price') ? v.unit_price : previous?.unitPrice,
            compareAtPrice:Object.prototype.hasOwnProperty.call(v,'compare_at_price') ? v.compare_at_price : previous?.compareAtPrice,
            unitReference:reference,
            sellingPlanRequired:Boolean(p.requires_selling_plan || previous?.sellingPlanRequired)
          };
        });
        return {...saved,...p,variants,collections:saved?.collections || []};
      });
      const remoteIDs=new Set(remote.map(p => String(p.id)));
      render(slot,config,choose([...remote,...config.products.filter(p => !remoteIDs.has(String(p.id)))],items,8,config));
    });
  }
  window.CandyPeakCartRecommendations={refresh};
  let pending=false;
  const schedule=() => { if (pending) return; pending=true; requestAnimationFrame(() => { pending=false;refresh(); }); };
  document.addEventListener('cp:cart:updated',event => refresh(event.detail?.cart));
  document.addEventListener('cp:cart:opened',schedule);
  document.addEventListener('shopify:section:load',schedule);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(el => el.nodeType === 1 && (el.matches(slotSelector) || el.querySelector(slotSelector))))) schedule();
  }).observe(document.body,{childList:true,subtree:true});
  refresh();
})();

